/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { ComponentProps, FC, useEffect } from "react";
import * as _ from "lodash";
import Markdown from "react-markdown";

import {
  Analysis,
  Button,
  Charts,
  CloseOutlined,
  Col,
  FullscreenButton,
  Grid,
  Icon,
  message,
  Popover,
  Row,
  SwitchWithLabel,
  Tooltip
} from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { ChartEntity, Enterprise } from "@ctra/api";
import { GAEvent } from "@ctra/analytics";
import { useChartContext } from "@ctra/charts";
import { isMutableRef, resizeBase64Image } from "@ctra/utils";

import { ChartProps, ChartViewFilter, useChartAPI, useChartFilters } from "@chart";
import { GACategories } from "@analytics";

import { testIds } from "../testing";
import { AnomalyToggle } from "../AnomalyToggle";
import styles from "./ChartExtra.module.less";

export type ChartExtraProps = Omit<ChartProps, "filters">;

const {
  chart: { compare, logScaling, logScalingDisabled },
  analytics: {
    feedback: { title }
  }
} = Content;

/**
 * Extra tools for chart components: full screen/chart comparison
 * @param {(() => void) | undefined} handleCompare
 * @param {(() => void) | undefined} handleClose
 * @param {{}} viewOptions
 * @param {ChartVariant | undefined} variant
 * @param {Partial<Pick<ContainerConfig<Options, Plot<O>>, "onReady" | "onEvent"> & Options> | undefined} config
 * @returns {JSX.Element}
 * @constructor
 */
export const ChartExtra: FC<ChartExtraProps> = ({
  handleCompare,
  handleClose,
  viewOptions = {},
  variant
}) => {
  const { t } = useTranslation();
  const { md } = Grid.useBreakpoint();

  const {
    ref,
    values: { config, scaleValidation },
    api: { setContextConfig }
  } = useChartContext();

  const {
    timePeriod,
    api: { getMetadata }
  } = useChartFilters();

  const {
    charts: [{ entity: chart }],
    meta,
    api,
    metricVariants: [
      {
        id: variantID,
        dataProperties: { typeName }
      }
    ]
  } = useChartAPI();

  const { supportedfarmIDs: farmIDs } = getMetadata(variantID);
  const { changeView } = api;
  const { supportedViews } = meta;
  const { view } = chart as ChartEntity;

  /**
   * Show toast notification when logarithmic scale is disabled due to invalid data
   */
  useEffect(() => {
    if (scaleValidation?.wasRequested && !scaleValidation?.isValid) {
      message.warning({
        content: t<string>(logScalingDisabled),
        duration: 5,
        key: "log-scale-warning"
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleValidation]);

  /**
   * Handle chart view changes
   * @param view
   */
  const onViewChange: ComponentProps<typeof ChartViewFilter>["onViewChange"] = (view) => {
    GAEvent(GACategories.configurator, "Change chart view", JSON.stringify({ view }));

    if (view === "table" && isMutableRef<any>(ref)) {
      ref.current = null;
    }

    changeView(view);
  };

  return (
    <Row
      justify="space-around"
      gutter={[8, 0]}
      align="middle"
      className={styles.Wrapper}
      onClick={(evt) => {
        evt.stopPropagation();
      }}
    >
      <Col>
        <Tooltip title={!scaleValidation?.isValid ? t<string>(logScalingDisabled) : undefined}>
          <SwitchWithLabel
            defaultChecked={_.get(config, ["yAxis", "type"]) === "log"}
            disabled={!scaleValidation?.isValid}
            onChange={(checked) => {
              if (!checked || scaleValidation?.isValid) {
                setContextConfig((old) => ({
                  ...old,
                  yAxis: {
                    ...old.yAxis,
                    type: checked ? "log" : void 0
                  }
                }));
              }
            }}
            size="small"
            label={t<string>(logScaling)}
          />
        </Tooltip>
      </Col>
      {viewOptions.showAnnotations && (_.isEqual(variant, Charts.ChartVariant.V3) || viewOptions.zoomed) && (
        <AnomalyToggle />
      )}
      {isMutableRef<any>(ref) && (
        <Col>
          <Button
            type={"primary"}
            onClick={async () => {
              const base64 = await resizeBase64Image(ref.current.toDataURL("image/png"), 1280);

              Enterprise.history.push({
                state: {
                  feedback: {
                    sourceID: chart.id,
                    sourceType: "Chart",
                    context: {
                      timePeriod,
                      typeName
                    },
                    base64Content: base64
                  }
                }
              });
            }}
          >
            {t(title)}
          </Button>
        </Col>
      )}
      {viewOptions.showSupportedViews && _.size(supportedViews) > 1 && (
        <Col className={styles.ViewFilter}>
          <ChartViewFilter views={supportedViews} view={view} onViewChange={onViewChange} />
        </Col>
      )}
      {handleCompare && farmIDs && (
        <Popover
          content={<Markdown>{t<string>(compare)}</Markdown>}
          placement="topRight"
          overlayClassName={styles.Popover}
        >
          <Button
            data-testid={testIds.chartExtra.compareCharts}
            onClick={() => {
              handleCompare();
            }}
            icon={<Icon component={Analysis} />}
          />
        </Popover>
      )}
      {/* exit full screen button */}
      {viewOptions.zoomed && md && (
        <FullscreenButton
          exit={true}
          data-testid={testIds.chartExtra.zoom}
          onClick={() => {
            Enterprise.history.push({
              state: {}
            });
          }}
        />
      )}
      {_.isFunction(handleClose) && <CloseOutlined className={styles.Close} onClick={handleClose} />}
    </Row>
  );
};
